/* eslint-disable eqeqeq */

import { useContext, useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";

import {
  Alert,
  Checkbox,
  NumberInput,

  Group,
  Modal,
  MultiSelect,
  Select,
  Space,
} from "@mantine/core";

import { Button } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

import { DocumentData, collectionGroup, getDoc, getDocs, doc, getFirestore, query, where, collection, orderBy, limit } from "firebase/firestore";

import { Customer, CustomerType, CustomClaims, KYC, NotificationMSG, Order, } from "../types";
import { ConfigContext, MyUserDataContext } from "../contexts";




type CustomerExportForm = {
  customerType: Exclude<CustomerType, "other"> | "all";
  segments: string[];
  products: string[];
  limit: number | string;
  randomize: boolean;
};

const GenerateKycCSVModal = (props: any) => {
  const userContext = useContext(MyUserDataContext);
  const config = useContext(ConfigContext);
  const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
  const allowedSegments = [...new Set([
    userClaims.segment,
    ...(userClaims.availableSegments ?? []),
  ].filter((segment): segment is string => Boolean(segment?.trim())).map((segment) => segment.trim()))];
  const segmentOptions = userClaims.kfh_oper
    ? allowedSegments
    : userClaims.admin
      ? [...new Set((config.segments?.segmentList ?? []).map((segment) => segment.name).filter(Boolean))]
      : [];

  const opened: boolean = props.opened;
  const setOpened = props.setOpened;
  const [confirming, setConfirming] = useState<boolean>(false);
  const [msg, setMsg] = useState<NotificationMSG | null>(null);
  const [downloadReady, setDownloadReady] = useState<boolean>(false);

  const db = getFirestore();

  const headers = [
    //personal info stuff
    { label: "ID", key: "customerObj.id" },
    { label: "Civil ID", key: "customerObj.civilID" },
    { label: "Type", key: "type" },
    { label: "Customer Type", key: "customerObj.customerType" },
    { label: "Name (en)", key: "customerObj.name_en" },
    { label: "Name (ar)", key: "customerObj.name_ar" },
    { label: "Segment", key: "customerObj.segment" },
    { label: "Mobile", key: "customerObj.mobile" },
    { label: "Address", key: "customerObj.address" },
    { label: "RM", key: "customerObj.rm_email" },
    { label: "Signed Consent", key: "customerObj.agreements.kfh_rm_access_consent" },
    { label: "active", key: "customerObj.has_active_products" },
    { label: "KYC Status", key: "kyc_status" },
    { label: "Products", key: "products" },

  ];


  const [data, setData] = useState<DocumentData[]>([]);

  const getValueByPath = (item: DocumentData, path: string) => {
    return path.split(".").reduce<any>((value, key) => value?.[key], item) ?? "";
  };

  const downloadExcel = () => {
    if (data.length === 0) return;

    const labels = headers.map((header) => header.label);
    const rows = data.map((item) => headers.map((header) => getValueByPath(item, header.key)));
    const worksheet = XLSX.utils.aoa_to_sheet([labels, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    const date = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
    XLSX.writeFile(workbook, `customers_${date}.xlsx`, { bookType: "xlsx" });
  };

  const getCustomerQuery = (data: { segments: string[] }) => {




    let q = query(collection(db, "customers"));

    if (userClaims.kfh_rm || userClaims.kfh_oper) {
      if (allowedSegments.length === 0) {
        throw new Error("No customer segments are assigned to this user.");
      }

      const selectedSegments = data.segments.length > 0
        ? data.segments.filter((segment) => allowedSegments.includes(segment))
        : allowedSegments;

      if (selectedSegments.length !== (data.segments.length || allowedSegments.length)) {
        throw new Error("One or more selected segments are not available to this user.");
      }

      q = selectedSegments.length === 1
        ? query(q, where("segment", "==", selectedSegments[0]))
        : query(q, where("segment", "in", selectedSegments));
    } else if (userClaims.admin && data.segments.length > 0) {
      const selectedSegments = data.segments.filter((segment) => segmentOptions.includes(segment));

      if (selectedSegments.length !== data.segments.length) {
        throw new Error("One or more selected segments are invalid.");
      }

      q = selectedSegments.length === 1
        ? query(q, where("segment", "==", selectedSegments[0]))
        : query(q, where("segment", "in", selectedSegments));
    }




    return q;
  };

  const [productOptions, setProductOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);
  const segmentScope = allowedSegments.join("\n");

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLoadingProducts(true);
    setProductOptions([]);
    setProductError(null);
    const loadProducts = async () => {
      try {
        const options = new Map<string, string>();
        try {
          if (!userContext.customerCollectionSearchKey) throw new Error("Customer search key is unavailable.");
          const parameters = new URLSearchParams({
            q: "*",
            query_by: "name_en",
            facet_by: "tags_object_arr.code",
            facet_return_parent: "tags_object_arr.code",
            max_facet_values: "1000",
            per_page: "0",
          });
          if (userClaims.kfh_rm || userClaims.kfh_oper) {
            if (!allowedSegments.length) throw new Error("No customer segments are assigned to this user.");
            parameters.set("filter_by", `segment:=[${allowedSegments.map((segment) =>
              "`" + segment.replace(/`/g, "\\`") + "`").join(",")}]`);
          }
          const response = await fetch(
            `https://${import.meta.env.VITE_TYPESENSE_SEARCH_HOST}/collections/${encodeURIComponent(import.meta.env.VITE_TYPESENSE_CUSTOMERS_COLLECTION)}/documents/search?${parameters}`,
            { headers: { "X-TYPESENSE-API-KEY": userContext.customerCollectionSearchKey } },
          );
          if (!response.ok) throw new Error("Unable to load product facets.");
          const result = await response.json();
          if (!Array.isArray(result.facet_counts) || !result.facet_counts.length) {
            throw new Error("Product facets are unavailable.");
          }
          const counts = result.facet_counts.find((facet: any) =>
            facet.field_name === "tags_object_arr.code")?.counts;
          if (!Array.isArray(counts)) throw new Error("Customer tag facets are unavailable.");
          // The tag-array facet contains code/key only. Fetch one small example
          // per code to identify product tags and obtain their display labels.
          const searches = counts.map((count: any) => {
            const tagFilter = "tags_object_arr.code:=`" + String(count.value).replace(/`/g, "\\`") + "`";
            return {
              collection: import.meta.env.VITE_TYPESENSE_CUSTOMERS_COLLECTION,
              q: "*",
              query_by: "name_en",
              filter_by: [parameters.get("filter_by"), tagFilter].filter(Boolean).join(" && "),
              include_fields: "tags",
              per_page: 1,
            };
          });
          for (let offset = 0; offset < searches.length; offset += 40) {
            const batch = searches.slice(offset, offset + 40);
            const examplesResponse = await fetch(
              `https://${import.meta.env.VITE_TYPESENSE_SEARCH_HOST}/multi_search`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-TYPESENSE-API-KEY": userContext.customerCollectionSearchKey,
                },
                body: JSON.stringify({ searches: batch }),
              },
            );
            if (!examplesResponse.ok) throw new Error("Unable to load product tag labels.");
            const examples = await examplesResponse.json();
            if (!Array.isArray(examples.results) || examples.results.length !== batch.length) {
              throw new Error("Product tag examples are unavailable.");
            }
            examples.results.forEach((example: any, index: number) => {
              if (example.error || !example.hits?.length) throw new Error("Product tag example is unavailable.");
              const tags = example.hits[0].document?.tags as Customer["tags"];
              Object.entries(tags ?? {}).forEach(([key, tag]) => {
                if (tag?.service && tag.code === counts[offset + index].value) {
                  options.set(key, tag.title || tag.shortName || key);
                }
              });
            });
          }
        } catch {
          // Older indexes may not support nested parent facets. Preserve the
          // complete product list by falling back to authorized Firestore data.
          options.clear();
          const customers = await getDocs(getCustomerQuery({ segments: [] }));
        customers.docs.forEach((customer) => {
          Object.entries((customer.data() as Customer).tags ?? {}).forEach(([key, tag]) => {
            if (tag?.service) options.set(key, tag.title || tag.shortName || key);
          });
        });
        }
        if (!cancelled) setProductOptions(Array.from(options, ([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label)));
      } catch (error: any) {
        if (!cancelled) setProductError(error.message || "Unable to load customer products.");
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    };
    void loadProducts();
    return () => { cancelled = true; };
    // The product list covers the user's full authorized segment scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, segmentScope, userClaims.kfh_rm, userClaims.kfh_oper, userClaims.admin, userContext.customerCollectionSearchKey]);

  const handleSubmitGenerateCSV = async (data: CustomerExportForm) => {
    const customerLimit = data.limit === "" ? undefined : Number(data.limit);
    if (customerLimit !== undefined && (!Number.isSafeInteger(customerLimit) || customerLimit < 1)) {
      throw new Error("Limit must be a positive whole number.");
    }

    let q = getCustomerQuery(data);
    // Type/product filtering and random sampling need all matches before the cap.
    if (customerLimit !== undefined && !data.randomize && data.products.length === 0 && data.customerType === "all") {
      q = query(q, limit(customerLimit));
    }
    const querySnapshot = await getDocs(q);
    let customerDocs = querySnapshot.docs.filter((document) => {
      const customer = document.data() as Customer;
      return (data.customerType === "all" || customer.customerType === data.customerType)
        && (data.products.length === 0 || data.products.some((product) => Boolean(customer.tags?.[product]?.service)));
    });
    if (data.randomize) {
      for (let i = customerDocs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [customerDocs[i], customerDocs[j]] = [customerDocs[j], customerDocs[i]];
      }
    }
    if (customerLimit !== undefined) {
      customerDocs = customerDocs.slice(0, customerLimit);
    }
    const dataList: DocumentData[] = [];


    // Fetch each customer's fund data in parallel so Active reflects current
    // positive fund units instead of the stored has_active_products flag.
    const customerPromises = customerDocs.map(async (document) => {
      const fundDataSnapshot = await getDocs(collection(db, "customers", document.id, "fund_data"));
      return [document.data(), fundDataSnapshot] as const;

    });


    // Fetch all customers and their KYCs in parallel
    const customerAndKYCDocs = await Promise.all(customerPromises);

    for (let i = 0; i < customerDocs.length; i++) {

      let d = customerDocs[i].data();
      let customerAndKYCDocResult = customerAndKYCDocs[i];

      if (customerAndKYCDocResult) {
        let [customerData, fundDataSnapshot] = customerAndKYCDocResult;
        const hasPositiveFundUnits = fundDataSnapshot.docs.some((fundDocument) => {
          const eunits = fundDocument.data().eunits;
          return typeof eunits === "number" && Number.isFinite(eunits) && eunits > 0;
        });
        d.customerObj = {
          ...customerData,
          agreements: {
            ...(customerData.agreements ?? {}),
            kfh_rm_access_consent: customerData.agreements?.kfh_rm_access_consent === true,
          },
          has_active_products: hasPositiveFundUnits,
        } as Customer;
        d.products = Object.entries((customerData as Customer).tags ?? {})
          .filter(([, tag]) => tag?.service)
          .map(([key, tag]) => tag.title || tag.shortName || key)
          .join(", ");
        console.log("customerData  Is HERE =======>>>", customerData);


      }
      dataList.push(d);
    }
    setData(dataList);

    setDownloadReady(true);
  }
  const form = useForm<CustomerExportForm>({
    initialValues: {
      customerType: "all",
      segments: [],
      products: [],
      limit: "",
      randomize: false,
    },
    validate: {
      limit: (value) => value === "" || (Number.isSafeInteger(Number(value)) && Number(value) > 0)
        ? null
        : "Enter a positive whole number",
    },
  });

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => {
          setMsg(null);
          setOpened(false);
          setConfirming(false)
        }}
        title="Generate CSV"
      >
        <form
          onSubmit={form.onSubmit(async (values) => {
            console.log(values);

            setDownloadReady(false);
            setData([]);
            // setConfirmDialogError(null);
            setMsg(null);
            // props?.setPending(true)

            setConfirming(true);
            console.log("yeaaa we are here");

            try {

              await handleSubmitGenerateCSV(values)

              // setIsError(true);
              setMsg({ type: "success", message: "CSV Created Successfully" });
            } catch (error: any) {
              // setIsError(true);
              console.log(error);
              // setMsg(error.message);
              // setMsg({type:"error",msg:});
              setMsg({ type: "error", message: error.message });


              // setConfirmDialogError(error.message);
            }

            setConfirming(false);

          })}
        >

          <Select
            label="Customer type"
            data={[
              { value: "all", label: "All" },
              { value: "individual", label: "Individual" },
              { value: "company", label: "Company" },
              { value: "trust", label: "Trust" },
            ]}
            allowDeselect={false}
            disabled={confirming}
            {...form.getInputProps("customerType")}
          />

          <Space h="md" />

          {(userClaims.kfh_oper || userClaims.admin) && (
            <MultiSelect
              label="Segments"
              placeholder="All available segments"
              data={segmentOptions}
              searchable
              clearable
              {...form.getInputProps("segments")}
            />
          )}

          <Space h="md" />

          <MultiSelect
            label="Products"
            description="Include customers with any selected product tag, within the selected segments."
            placeholder={loadingProducts ? "Loading products..." : "All products"}
            data={productOptions}
            searchable
            clearable
            disabled={loadingProducts || confirming}
            error={productError}
            {...form.getInputProps("products")}
          />

          <Space h="md" />

          <NumberInput
            label="Limit"
            description="Maximum number of customers to export. Leave blank to export all matches."
            placeholder="All matching customers"
            min={1}
            max={Number.MAX_SAFE_INTEGER}
            allowDecimal={false}
            allowNegative={false}
            disabled={confirming}
            {...form.getInputProps("limit")}
          />

          <Checkbox
            mt="md"
            label="Randomize"
            description="Shuffle matching customers before applying the limit."
            disabled={confirming}
            {...form.getInputProps("randomize", { type: "checkbox" })}
          />

          <Space h="md" />

          <Group>
            <Button color="grape" type="submit" loading={confirming}>
              Generate CSV
            </Button>
          </Group>

          <Space h="xl" />

          {msg && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color={msg.type == "success" ? "green" : "red"}
              title={msg.type == "success" ? "Success" : "Failure"}
            >


              {msg.message}
              <br />
              {downloadReady && (
                <Group mt="xs">
                  <CSVLink data={data} headers={headers}>
                    Download CSV
                  </CSVLink>
                  <Button size="xs" variant="light" onClick={downloadExcel}>
                    Download Excel
                  </Button>
                </Group>
              )}

            </Alert>
          )}

        </form>
      </Modal>
    </>
  );
}

export default GenerateKycCSVModal;
